import React, { useState } from 'react';
import { UserPlus, Shield, Mail, MoreHorizontal, Crown } from 'lucide-react';
import { PageHeader, PageShell, SectionCard, Badge } from '../components/PeecUI';

interface MembersPageProps {
  workspaceId: string;
}

const members = [
  { id: 'm1', name: '陈文博', email: 'chen.wenbo@tradingbase.ai', role: 'owner', avatar: 'CW', joined: '2025-09-01', lastActive: '刚刚', online: true },
  { id: 'm2', name: '林晓燕', email: 'lin.xiaoyan@tradingbase.ai', role: 'admin', avatar: 'LX', joined: '2025-09-15', lastActive: '1小时前', online: true },
  { id: 'm3', name: 'Sarah K.', email: 'sarah.k@tradingbase.ai', role: 'editor', avatar: 'SK', joined: '2025-11-20', lastActive: '昨天', online: false },
  { id: 'm4', name: '王强', email: 'wang.qiang@tradingbase.ai', role: 'viewer', avatar: 'WQ', joined: '2026-01-10', lastActive: '3天前', online: false },
  { id: 'm5', name: 'Raj Patel', email: 'raj.patel@tradingbase.ai', role: 'viewer', avatar: 'RP', joined: '2026-02-28', lastActive: '上周', online: false },
];

const invites = [
  { email: 'new.member@example.com', role: 'editor', sent: '2026-06-20', expires: '2026-06-27' },
];

const roleLabel = (role: string) => {
  switch (role) {
    case 'owner': return { label: '所有者', tone: 'purple' as const };
    case 'admin': return { label: '管理员', tone: 'blue' as const };
    case 'editor': return { label: '编辑者', tone: 'green' as const };
    default: return { label: '查看者', tone: 'slate' as const };
  }
};

const avatarColors = ['bg-violet-100 text-violet-700', 'bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700'];

const MembersPage: React.FC<MembersPageProps> = () => {
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  return (
    <PageShell>
      <PageHeader title="团队成员" description="管理工作空间访问权限与角色分配" />

      <div className="mb-5 flex items-center justify-between">
        <div className="text-sm text-slate-500">{members.length} 位成员 · {invites.length} 份待接受邀请</div>
        <button
          onClick={() => setShowInviteForm(v => !v)}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-950 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors focus-visible:ring-2 focus-visible:ring-slate-400 outline-none"
        >
          <UserPlus size={15} /> 邀请成员
        </button>
      </div>

      {showInviteForm && (
        <div className="mb-5 rounded-xl border border-violet-200 bg-violet-50/50 p-4 flex items-center gap-3">
          <Mail size={16} className="text-violet-500 flex-shrink-0" />
          <input
            type="email"
            placeholder="输入邮箱地址"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            className="flex-1 bg-transparent text-sm text-slate-900 placeholder-slate-400 outline-none"
          />
          <select className="text-xs border border-slate-200 bg-white rounded-md px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400">
            <option>查看者</option>
            <option>编辑者</option>
            <option>管理员</option>
          </select>
          <button
            onClick={() => { setShowInviteForm(false); setInviteEmail(''); }}
            className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-violet-700 transition-colors"
          >
            发送邀请
          </button>
        </div>
      )}

      <SectionCard title="当前成员" subtitle="工作空间的所有成员及其权限">
        <div className="divide-y divide-slate-100">
          {members.map((member, i) => {
            const { label, tone } = roleLabel(member.role);
            return (
              <div key={member.id} className="flex items-center gap-3 p-4 hover:bg-slate-50/70 transition-colors">
                <div className="relative flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${avatarColors[i % avatarColors.length]}`}>
                    {member.avatar}
                  </div>
                  {member.online && (
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-400 border-2 border-white rounded-full" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-slate-900">{member.name}</span>
                    {member.role === 'owner' && <Crown size={12} className="text-amber-500" />}
                    <Badge tone={tone}>{label}</Badge>
                  </div>
                  <div className="text-xs text-slate-400">{member.email} · 最后活跃：{member.lastActive}</div>
                </div>
                {member.role !== 'owner' && (
                  <button aria-label="更多操作" className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors focus-visible:ring-2 focus-visible:ring-violet-400 outline-none">
                    <MoreHorizontal size={15} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </SectionCard>

      {invites.length > 0 && (
        <SectionCard className="mt-5" title="待接受邀请" subtitle="已发出但尚未接受的邀请">
          <div className="divide-y divide-slate-100">
            {invites.map((inv, i) => (
              <div key={i} className="flex items-center justify-between p-4">
                <div>
                  <div className="text-sm font-medium text-slate-800">{inv.email}</div>
                  <div className="text-xs text-slate-400">发送于 {inv.sent} · 过期时间 {inv.expires}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge tone="amber">待确认</Badge>
                  <button className="text-xs text-slate-400 hover:text-rose-500 transition-colors">撤销</button>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      <SectionCard className="mt-5" title="角色说明" subtitle="各角色的权限范围">
        <div className="grid gap-3 p-4 sm:grid-cols-2">
          {[
            { role: '所有者', desc: '完整控制工作空间、计费、成员和数据', icon: <Crown size={14} className="text-amber-500" /> },
            { role: '管理员', desc: '管理成员、项目和 API 密钥，不含计费', icon: <Shield size={14} className="text-blue-500" /> },
            { role: '编辑者', desc: '可编辑项目配置和查看所有数据', icon: <Shield size={14} className="text-emerald-500" /> },
            { role: '查看者', desc: '只读访问，不可修改任何配置', icon: <Shield size={14} className="text-slate-400" /> },
          ].map((item) => (
            <div key={item.role} className="flex items-start gap-2 rounded-lg bg-slate-50 p-3">
              {item.icon}
              <div>
                <div className="text-sm font-medium text-slate-800">{item.role}</div>
                <div className="text-xs text-slate-500 mt-0.5">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    </PageShell>
  );
};

export default MembersPage;
